import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import classNames from "classnames";
import {
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Row,
    Col,
    Button,
} from "reactstrap";
import {
    Tooltip,
    OverlayTrigger
} from "react-bootstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import SweetAlert from "react-bootstrap-sweetalert";
import NotificationAlert from "react-notification-alert";
import rest from "../../API/api";
import { authenticatedFetch } from "../../services/authService";
import { UserLoginContext } from "../../context/UserLoginContext";
import { EventsContext } from "../../context/EventsContext";

const EditRegistrationsTable = () => {
    const { id } = useParams(); // Event ID from URL
    const [eventsState, setEventsState] = useContext(EventsContext);
    const [userState, setUserState] = useContext(UserLoginContext);
    const [alert, setalert] = useState(null);
    const history = useHistory();
    const [data, setData] = useState([]);
    const [eventData, setEventData] = useState(null);
    const [loading, setLoading] = useState(true);
    const notificationAlertRef = useRef(null);

    const notify = (type, message) => {
        if (!notificationAlertRef.current) {
            console.warn('Notification ref not ready');
            return;
        }
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    // Load event data and registrations on mount
    useEffect(() => {
        const loadEventAndRegistrations = async () => {
            if (!userState.status || !id) {
                setLoading(false);
                return;
            }

            try {
                const tempURL = `${rest.events}/${id}`;
                const eventResponse = await authenticatedFetch(tempURL, {
                    method: 'GET'
                });

                if (!eventResponse.ok) {
                    console.error('Failed to fetch event:', eventResponse.status);
                    setLoading(false);
                    return;
                }

                const event = await eventResponse.json();
                setEventData(event);

                // Update context with current event
                setEventsState((prevState) => ({
                    ...prevState,
                    theEvent: event
                }));

                setLoading(false);
            } catch (error) {
                console.error('Error fetching event:', error);
                setLoading(false);
            }
        };

        loadEventAndRegistrations();
    }, [userState.status, id]);

    // Update table data whenever event data changes
    useEffect(() => {
        if (eventData && eventData.Registrations && Array.isArray(eventData.Registrations)) {
            const tableData = eventData.Registrations.map((registration, i) => {
                return {
                    id: registration._id,
                    name: registration.Name || "",
                    phone: registration.Phone || "",
                    email: registration.Email || "",
                    signedwaiver: registration.SignedWaiver ? "Yes" : "No",
                    actions: (
                        <div className="actions-right">
                            {/* Edit Registration */}
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`edit_${i}`}>Edit Registration</Tooltip>}
                            >
                                <span>
                                    <Button
                                        onClick={async () => {
                                            // Navigate to edit registration page
                                            // For now, we'll use the add registration page with the registration ID
                                            // You may want to create a separate EditRegistration component
                                            let tURL = `/admin/editregistration/${registration._id}`;
                                            await history.push(tURL);
                                        }}
                                        color="warning"
                                        size="sm"
                                        className={classNames("btn-icon btn-link like")}
                                    >
                                        <i className="tim-icons icon-pencil"/>
                                    </Button>{" "}
                                </span>
                            </OverlayTrigger>

                            {/* Delete Registration */}
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`delete_${i}`}>Delete Registration</Tooltip>}
                            >
                                <span>
                                    <Button
                                        onClick={async () => {
                                            const registrationIndex = eventData.Registrations.findIndex(r => r._id === registration._id);
                                            if (registrationIndex !== -1) {
                                                _deleteRegistrationAlert(registration._id, eventData.Registrations, registrationIndex);
                                            }
                                        }}
                                        color="danger"
                                        size="sm"
                                        className={classNames("btn-icon btn-link like")}
                                    >
                                        <i className="tim-icons icon-simple-remove"/>
                                    </Button>{" "}
                                </span>
                            </OverlayTrigger>
                        </div>
                    )
                };
            });
            setData(tableData);
        } else {
            setData([]);
        }
    }, [eventData]);

    const _delete_registration = async (registrationId) => {
        try {
            const tempURL = `${rest.events}/registration/${registrationId}`;
            const response = await authenticatedFetch(tempURL, {
                method: 'DELETE'
            });

            if (!response.ok) {
                console.error('Failed to delete registration:', response.status);
                return { success: false, error: 'Failed to delete registration' };
            }

            // Reload event data after deletion
            const eventURL = `${rest.events}/${id}`;
            const eventResponse = await authenticatedFetch(eventURL, {
                method: 'GET'
            });

            if (eventResponse.ok) {
                const updatedEvent = await eventResponse.json();
                setEventData(updatedEvent);

                // Update context
                setEventsState((prevState) => ({
                    ...prevState,
                    theEvent: updatedEvent
                }));

                return { success: true };
            } else {
                return { success: false, error: 'Failed to reload event data' };
            }
        } catch (error) {
            console.error('Error deleting registration:', error);
            return { success: false, error: error.message };
        }
    };

    const _deleteRegistrationAlert = async (registrationId, registrations, index) => {
        setalert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, delete it!"
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                title="Are you sure?"
                style={{ display: "block" }}
                onConfirm={async () => {
                    setalert(null);

                    // Delete the registration
                    const result = await _delete_registration(registrationId);

                    if (result.success) {
                        notify("success", "Registration has been deleted successfully!");
                    } else {
                        notify("danger", "Error deleting registration: " + (result.error || "Unknown error"));
                    }
                }}
                onCancel={() => {
                    setalert(null);
                }}
            >
                Are you sure you want to delete this registration?
            </SweetAlert>
        );
    };

    if (!userState.status) {
        history.push("/admin/dashboard");
        return null;
    }

    if (loading) {
        return (
            <div className="content">
                <Row>
                    <Col md={12}>
                        <Card>
                            <CardHeader>
                                <h5 className="title">Loading...</h5>
                            </CardHeader>
                            <CardBody>
                                <p>Loading event registrations...</p>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }

    return (
        <>
            <div className="react-notification-alert-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <div className="content">
                <Col md={8} className="ml-auto mr-auto">
                    <h2 className="text-center">
                        {eventData ? `${eventData.Title} - Registrations` : 'Event Registrations'}
                    </h2>
                    <p className="text-center">
                        Manage registrations for this event
                    </p>
                </Col>
                <Row className="mt-5">
                    <Col xs={12} md={12}>
                        <Card>
                            <CardHeader>
                                <Row>
                                    <Col md="6">
                                        <CardTitle tag="h4">
                                            Registrations ({eventData?.Registrations?.length || 0})
                                        </CardTitle>
                                    </Col>
                                    <Col md="6" className="text-right">
                                        <Button
                                            color="info"
                                            onClick={() => history.push(`/admin/addregistration/${id}`)}
                                            className="animation-on-hover"
                                        >
                                            <i className="tim-icons icon-simple-add"/> Add Registration
                                        </Button>
                                        <Button
                                            color="default"
                                            onClick={() => history.push("/admin/events")}
                                            className="animation-on-hover ml-2"
                                        >
                                            <i className="tim-icons icon-minimal-left"/> Back to Events
                                        </Button>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <ReactTable
                                    data={data}
                                    filterable
                                    columns={[
                                        {
                                            Header: "Name",
                                            accessor: "name"
                                        },
                                        {
                                            Header: "Phone",
                                            accessor: "phone"
                                        },
                                        {
                                            Header: "Email",
                                            accessor: "email"
                                        },
                                        {
                                            Header: "Signed Waiver",
                                            accessor: "signedwaiver"
                                        },
                                        {
                                            Header: "Actions",
                                            accessor: "actions",
                                            sortable: false,
                                            filterable: false
                                        }
                                    ]}
                                    defaultPageSize={5}
                                    showPaginationTop
                                    showPaginationBottom={false}
                                    className="-striped -highlight"
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                {alert}
            </div>
        </>
    );
};

export default EditRegistrationsTable;
